import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, DollarSign } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const Nomina = () => {
  const [payrolls, setPayrolls] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    empleado_id: '',
    periodo: new Date().toISOString().slice(0, 7),
    tipo_periodo: 'mensual',
    salario_base: 0,
    bonos: 0,
    deducciones: 0,
    pais: 'USA',
    horas_trabajadas: 0,
    horas_extra: 0
  });

  useEffect(() => {
    fetchPayrolls();
    fetchEmployees();
  }, []);

  const fetchPayrolls = async () => {
    try {
      const response = await api.get('/payroll');
      setPayrolls(response.data || []);
    } catch (error) {
      toast.error('Error al cargar nómina');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.data || []);
    } catch (error) {
      console.error('Error loading employees');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/payroll', formData);
      toast.success('Nómina creada');
      fetchPayrolls();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error');
    }
  };

  const resetForm = () => {
    setFormData({ empleado_id: '', periodo: new Date().toISOString().slice(0, 7), tipo_periodo: 'mensual', salario_base: 0, bonos: 0, deducciones: 0, pais: 'USA', horas_trabajadas: 0, horas_extra: 0 });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl flex items-center"><DollarSign className="mr-2" />Nómina</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild><Button><Plus className="mr-2 h-4 w-4" />Crear Nómina</Button></DialogTrigger>
                <DialogContent className="max-w-xl">
                  <DialogHeader><DialogTitle>Nueva Nómina</DialogTitle></DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div><Label>Empleado *</Label>
                      <Select value={formData.empleado_id} onValueChange={(value) => { const emp = employees.find(e => e.id === value); setFormData({...formData, empleado_id: value, salario_base: emp?.salario_base || 0}); }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar empleado" /></SelectTrigger>
                        <SelectContent>
                          {employees.map((emp) => <SelectItem key={emp.id} value={emp.id}>{emp.nombre_completo}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Período</Label><Input type="month" value={formData.periodo} onChange={(e) => setFormData({...formData, periodo: e.target.value})} /></div>
                      <div><Label>Tipo</Label>
                        <Select value={formData.tipo_periodo} onValueChange={(value) => setFormData({...formData, tipo_periodo: value})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="semanal">Semanal</SelectItem>
                            <SelectItem value="quincenal">Quincenal</SelectItem>
                            <SelectItem value="mensual">Mensual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Salario Base</Label><Input type="number" step="0.01" value={formData.salario_base} onChange={(e) => setFormData({...formData, salario_base: parseFloat(e.target.value) || 0})} /></div>
                      <div><Label>Bonos</Label><Input type="number" step="0.01" value={formData.bonos} onChange={(e) => setFormData({...formData, bonos: parseFloat(e.target.value) || 0})} /></div>
                      <div><Label>Deducciones</Label><Input type="number" step="0.01" value={formData.deducciones} onChange={(e) => setFormData({...formData, deducciones: parseFloat(e.target.value) || 0})} /></div>
                      <div><Label>País</Label>
                        <Select value={formData.pais} onValueChange={(value) => setFormData({...formData, pais: value})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="USA">USA</SelectItem><SelectItem value="RD">RD</SelectItem></SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit">Crear</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-8">Cargando...</div> : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Empleado</TableHead><TableHead>Período</TableHead><TableHead>Salario Base</TableHead><TableHead>Neto</TableHead><TableHead>Estado</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {payrolls.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center">No hay nóminas</TableCell></TableRow> : payrolls.map((payroll) => (
                    <TableRow key={payroll.id}>
                      <TableCell className="font-medium">{payroll.empleado?.nombre_completo}</TableCell>
                      <TableCell>{payroll.periodo}</TableCell>
                      <TableCell>${payroll.salario_base?.toFixed(2)}</TableCell>
                      <TableCell className="font-semibold">${payroll.total_neto?.toFixed(2)}</TableCell>
                      <TableCell><Badge>{payroll.estado}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Nomina;
